import {
    faCheck,
    faExclamation,
    faTimes,
    IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormError } from '@kcftech/design-system';
import { IJobResponse, JobStatus } from '@kcftech/prometheus-client';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { DateRelativeToNow } from 'components/date_time/DateRelativeToNow';
import { PagedJobsDataSource } from 'custom_hooks/data_hooks/data_source/jobDataSource';
import { JobFileDownloadButton } from 'pages/Settings/SetupTools/JobFileDownloadButton';
import { jobStatusToName, jobTypeToName } from 'pages/Settings/SetupTools/shared';
import { tokens } from 'styles/tokens';
import { getId } from 'utils/data';
import { Lookup } from 'utils/types';

type StatusInfo = Readonly<{ name: string; icon?: IconDefinition; color?: string }>;

const statusToInfo: Lookup<JobStatus, StatusInfo> = {
    Finished: {
        name: jobStatusToName[JobStatus.Finished],
        icon: faCheck,
        color: tokens.asset.normal,
    },
    FinishedWithErrors: {
        name: jobStatusToName[JobStatus.FinishedWithErrors],
        icon: faExclamation,
        color: tokens.asset.warning,
    },
    Errored: {
        name: jobStatusToName[JobStatus.Errored],
        icon: faTimes,
        color: tokens.asset.alarm,
    },
    Queued: { name: jobStatusToName[JobStatus.Queued] },
    Running: { name: jobStatusToName[JobStatus.Running] },
    Deferred: { name: jobStatusToName[JobStatus.Deferred] },
};

const getJobStatus = (status: JobStatus) => {
    const { name, icon, color } = statusToInfo[status];

    return (
        <div className="flex-row">
            {icon && color && (
                <FontAwesomeIcon icon={icon} color={color} className="pr-10" />
            )}
            {name}
        </div>
    );
};

const columns: TableColumn<IJobResponse>[] = [
    {
        id: 'hierarchy',
        header: () => 'Corporation / Location',
        value: ({ rowData: { Corporation, Location } }) =>
            `${Corporation.CorporationName} / ${Location.LocationName}`,
        primary: true,
        locked: true,
        sortOn: ['CorporationName', 'LocationName'],
    },
    {
        id: 'type',
        header: () => 'Type',
        value: ({ rowData: { Type } }) => jobTypeToName[Type],
        primary: true,
        sortOn: ['Type'],
    },
    {
        id: 'status',
        header: () => 'Status',
        value: ({ rowData: { Status } }) => getJobStatus(Status),
        sortOn: ['Status'],
    },
    {
        id: 'user',
        header: () => 'User',
        value: ({ rowData: { User } }) => `${User.UserFirstName} ${User.UserLastName}`,
        sortOn: ['UserFirstName', 'UserLastName'],
    },
    {
        id: 'date',
        header: () => 'Date',
        value: ({ rowData: { EnqueuedTime } }) => (
            <DateRelativeToNow date={EnqueuedTime} />
        ),
        sortOn: ['EnqueuedTime'],
    },
    {
        id: 'uploadedFile',
        header: () => 'Uploaded File',
        value: ({ rowData: { UploadedFileName, Id } }) =>
            UploadedFileName && (
                <JobFileDownloadButton
                    id={Id}
                    fileName={UploadedFileName}
                    isErrorFile={false}
                />
            ),
    },
    {
        id: 'errorFile',
        header: () => 'Errors',
        value: ({ rowData: { ErrorFileName, Status, Id } }) => (
            <div>
                {ErrorFileName && Status === JobStatus.FinishedWithErrors && (
                    <JobFileDownloadButton id={Id} fileName={ErrorFileName} isErrorFile />
                )}
                {Status === JobStatus.Errored && (
                    <FormError message="Unexpected error. Please try again." />
                )}
            </div>
        ),
    },
];

export type JobsTableProps = Readonly<{
    dataSource: PagedJobsDataSource;
}>;

export function JobsTable({ dataSource }: JobsTableProps) {
    return (
        <DataSourceTable
            dataSource={dataSource}
            rowKey={getId}
            columns={columns}
            noResultMessage="No jobs found."
        />
    );
}
